import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
} from "@mui/material";
import { Formik } from "formik";
import { useState } from "react";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";

import DefaultApiService from "../../../../../infrastructure/api/default";
import { wrapFormikHandleChange } from "../../../../../infrastructure/utils/formatters";
import {
  normalizeIban,
  validateBankAccount,
  validateCardNumber,
  validateIban,
} from "../../../contractor-registration/contractorBankFields";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";

const API_BASE = "BI/Administration/SolarentOfficialBankAccount";

const validationSchema = Yup.object().shape({
  bankAccountHolderName: Yup.string().required("نام صاحب حساب الزامی است"),
  bankName: Yup.string().required("نام بانک الزامی است"),
  bankAccountNumber: Yup.string().test(
    "bank-account",
    "شماره حساب نامعتبر است",
    (value) => !validateBankAccount(value)
  ),
  bankShebaNumber: Yup.string().test(
    "iban",
    "شماره شبا نامعتبر است",
    (value) => !validateIban(value)
  ),
  bankCardNumber: Yup.string().test(
    "card",
    "شماره کارت نامعتبر است",
    (value) => !value || !validateCardNumber(value)
  ),
});

const SolarentOfficialBankAccountModifyPage = () => {
  const data = useLoaderData("root") || {};
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const isCreate = id === "create";

  const onNavigateToListPage = () => {
    navigate("/BI/Administration/SolarentOfficialBankAccount");
  };

  const onSubmit = async (formValue, { setSubmitting }) => {
    setLoading(true);
    const payload = {
      id: isCreate ? 0 : Number(data.id || 0),
      title: formValue.title?.trim() || "",
      bankAccountHolderName: formValue.bankAccountHolderName?.trim(),
      bankName: formValue.bankName?.trim(),
      bankAccountNumber: formValue.bankAccountNumber,
      bankShebaNumber: normalizeIban(formValue.bankShebaNumber),
      bankCardNumber: formValue.bankCardNumber || "",
      isActive: Boolean(formValue.isActive),
    };

    try {
      const res = await DefaultApiService.modifyApi(API_BASE, payload);
      if (res?.isSuccess) {
        toast.success(res.message || "ذخیره شد.");
        onNavigateToListPage();
      } else {
        toast.error(res?.message || "ذخیره انجام نشد.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "خطایی رخ داده است.");
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>
      <Grid item xs={12} md={8}>
        <Card>
          <CardHeader title={isCreate ? "ثبت حساب بانکی" : "ویرایش حساب بانکی"} />
          <CardContent>
            <Formik
              initialValues={{
                title: "",
                bankAccountHolderName: "",
                bankName: "",
                bankAccountNumber: "",
                bankShebaNumber: "",
                bankCardNumber: "",
                isActive: false,
                ...data,
              }}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFieldValue,
              }) => (
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        name="title"
                        label="عنوان (اختیاری)"
                        placeholder="مثلاً حساب اصلی بانک ملی"
                        value={values.title || ""}
                        onChange={wrapFormikHandleChange(handleChange)}
                        onBlur={handleBlur}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        name="bankAccountHolderName"
                        label="نام صاحب حساب"
                        value={values.bankAccountHolderName || ""}
                        onChange={wrapFormikHandleChange(handleChange)}
                        onBlur={handleBlur}
                        error={
                          touched.bankAccountHolderName &&
                          Boolean(errors.bankAccountHolderName)
                        }
                        helperText={
                          touched.bankAccountHolderName &&
                          errors.bankAccountHolderName
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        name="bankName"
                        label="نام بانک"
                        value={values.bankName || ""}
                        onChange={wrapFormikHandleChange(handleChange)}
                        onBlur={handleBlur}
                        error={touched.bankName && Boolean(errors.bankName)}
                        helperText={touched.bankName && errors.bankName}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        name="bankAccountNumber"
                        label="شماره حساب"
                        value={values.bankAccountNumber || ""}
                        onChange={wrapFormikHandleChange(handleChange)}
                        onBlur={handleBlur}
                        error={
                          touched.bankAccountNumber &&
                          Boolean(errors.bankAccountNumber)
                        }
                        helperText={
                          touched.bankAccountNumber && errors.bankAccountNumber
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        name="bankShebaNumber"
                        label="شماره شبا"
                        value={values.bankShebaNumber || ""}
                        onChange={(e) => {
                          setFieldValue(
                            "bankShebaNumber",
                            normalizeIban(e.target.value)
                          );
                        }}
                        onBlur={handleBlur}
                        error={
                          touched.bankShebaNumber && Boolean(errors.bankShebaNumber)
                        }
                        helperText={
                          touched.bankShebaNumber && errors.bankShebaNumber
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        name="bankCardNumber"
                        label="شماره کارت (اختیاری)"
                        value={values.bankCardNumber || ""}
                        onChange={wrapFormikHandleChange(handleChange)}
                        onBlur={handleBlur}
                        error={
                          touched.bankCardNumber && Boolean(errors.bankCardNumber)
                        }
                        helperText={
                          touched.bankCardNumber && errors.bankCardNumber
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            name="isActive"
                            checked={Boolean(values.isActive)}
                            onChange={handleChange}
                          />
                        }
                        label="فعال برای درج در قرارداد متقاضی"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={loading || isSubmitting}
                        startIcon={
                          loading || isSubmitting ? (
                            <CircularProgress size={18} />
                          ) : null
                        }
                      >
                        ذخیره
                      </Button>
                      <Button sx={{ ml: 1 }} onClick={onNavigateToListPage}>
                        انصراف
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default SolarentOfficialBankAccountModifyPage;
